import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Building2,
  DollarSign,
  Server,
  Activity,
  UserCheck,
  RefreshCw,
  GraduationCap,
  BookOpen,
  Calendar,
  Settings,
  Globe,
} from "lucide-react";
import LoadingSpinner from "../components/common/LoadingSpinner";
import StatsCard from "../components/common/StatsCard";
import ProgressBar from "../components/common/ProgressBar";
import SyncTokenGenerator from "../components/superadmin/SyncTokenGenerator";
import ManualInvoiceGeneration from "../components/superadmin/ManualInvoiceGeneration";
import { systemAnalyticsService } from "../services/system-analytics";
import { formatKSh } from "../utils/currency";
import { useAuth } from "../hooks/useAuth";

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch system health data
  const {
    data: healthData,
    isLoading: healthLoading,
    error: healthError,
  } = useQuery({
    queryKey: ["system-health"],
    queryFn: async () => {
      const result = await systemAnalyticsService.getSystemHealth();
      return result;
    },
    refetchInterval: 30000,
    retry: 1,
  });

  // Date
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Fetch system analytics data
  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ["system-analytics", "30d"],
    queryFn: async () => {
      const result = await systemAnalyticsService.getSystemAnalytics("30d");
      return result;
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
  });

  // Fetch user activity data
  const { data: userActivityData, isLoading: activityLoading } = useQuery({
    queryKey: ["user-activity", "7d"],
    queryFn: async () => {
      const result =
        await systemAnalyticsService.getUserActivityAnalytics("7d");
      return result;
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
  });

  const isLoading = healthLoading || analyticsLoading || activityLoading;
  const hasError = healthError || analyticsError;

  const handleRefresh = () => {
    refetchAnalytics();
  };

  // The api.ts interceptor already extracts response.data, so healthData and analyticsData
  // are already the response.data objects (which have { success, data } structure)
  // We need to access the nested data property
  const systemHealth = healthData?.data;
  const analytics = analyticsData?.data;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
            <p className="text-slate-600 font-medium">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-2 w-fit mx-auto mb-4">
              <Server className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              Error Loading Dashboard Data
            </h3>
            <p className="text-slate-600 font-medium mb-6">
              Unable to fetch system data from the API. Please check your
              connection and try again.
            </p>
            <button
              onClick={handleRefresh}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
            >
              <RefreshCw className="h-5 w-5 text-blue-500" />
              <span className="text-slate-700">Retry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if we have the actual data structure - the API returns data directly
  const hasAnalytics = analytics || analyticsData;
  const hasHealth = systemHealth || healthData;

  // Only show data if we have it from the API
  if (!hasAnalytics || !hasHealth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-2 w-fit mx-auto mb-4">
              <Activity className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              No Data Available
            </h3>
            <p className="text-slate-600 font-medium mb-2">
              System analytics data is not available at the moment.
            </p>
            <div className="text-sm text-slate-500 space-y-1 mb-6">
              <p>Analytics: {hasAnalytics ? "Available" : "Missing"}</p>
              <p>Health: {hasHealth ? "Available" : "Missing"}</p>
            </div>
            <button
              onClick={handleRefresh}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
            >
              <RefreshCw className="h-5 w-5 text-blue-500" />
              <span className="text-slate-700">Refresh</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // The api.ts interceptor returns response.data, and the service returns response.data again
  // So analyticsData is already the inner data object, not {success, data}
  const analyticsToUse = analyticsData; // Use analyticsData directly
  const healthToUse = healthData; // Use healthData directly

  if (isLoading) {
    return (
      <div className="p-2 md:p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header Section */}

      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="text-right">
          <p className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            {currentDate}
          </p>
        </div>
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-3 shadow-lg">
              <Server className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Super Admin Dashboard
              </h1>
              <p className="text-slate-600 font-medium mt-1">
                Monitor and manage your entire education management system
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
          >
            <RefreshCw className="h-5 w-5 text-blue-500" />
            <span className="text-slate-700">Refresh Data</span>
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Settings className="w-6 h-6 text-purple-500" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate("/terms")}
            className="bg-gradient-to-r from-purple-400 to-indigo-500 hover:from-purple-500 hover:to-indigo-600 text-white p-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex flex-col items-center gap-3"
          >
            <Calendar className="w-8 h-8" />
            <div className="text-center">
              <div className="font-bold text-lg">Manage Terms</div>
              <div className="text-xs opacity-90">Academic Terms</div>
            </div>
          </button>

          <button
            onClick={() => navigate("/branches")}
            className="bg-gradient-to-r from-blue-400 to-cyan-500 hover:from-blue-500 hover:to-cyan-600 text-white p-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex flex-col items-center gap-3"
          >
            <Building2 className="w-8 h-8" />
            <div className="text-center">
              <div className="font-bold text-lg">Manage Branches</div>
              <div className="text-xs opacity-90">Branch Management</div>
            </div>
          </button>

          <button
            onClick={() => navigate("/users")}
            className="bg-gradient-to-r from-emerald-400 to-green-500 hover:from-emerald-500 hover:to-green-600 text-white p-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex flex-col items-center gap-3"
          >
            <Users className="w-8 h-8" />
            <div className="text-center">
              <div className="font-bold text-lg">Manage Users</div>
              <div className="text-xs opacity-90">User Management</div>
            </div>
          </button>

          <button
            onClick={() => navigate("/landing-page")}
            className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white p-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex flex-col items-center gap-3"
          >
            <Globe className="w-8 h-8" />
            <div className="text-center">
              <div className="font-bold text-lg">Landing Page</div>
              <div className="text-xs opacity-90">Website Content</div>
            </div>
          </button>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Users</p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {analyticsToUse?.user?.totalUsers?.toLocaleString() || "0"}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-2 shadow-lg">
              <Users className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Active Users</p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {analyticsToUse?.user?.activeUsers?.toString() || "0"}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-2 shadow-lg">
              <UserCheck className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Students</p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {analyticsToUse?.academic?.totalStudents?.toLocaleString() ||
                  "0"}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-2 shadow-lg">
              <GraduationCap className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Teachers</p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {analyticsToUse?.academic?.totalTeachers?.toString() || "0"}
              </p>
            </div>
            <div className="bg-gradient-to-br from-indigo-300 to-indigo-400 rounded-2xl p-2 shadow-lg">
              <BookOpen className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Academic & Financial Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Classes</p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {analyticsToUse?.academic?.totalClasses?.toString() || "0"}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-2 shadow-lg">
              <Building2 className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Courses</p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {analyticsToUse?.academic?.totalCourses?.toString() || "0"}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-2 shadow-lg">
              <BookOpen className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Revenue</p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {formatKSh(analyticsToUse?.financial?.totalRevenue || 0)}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-2 shadow-lg">
              <DollarSign className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">
                Collection Rate
              </p>
              <p className="text-xl font-bold text-slate-900 mt-2">
                {analyticsToUse?.financial?.collectionRate || 0}%
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-2 shadow-lg">
              <Activity className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* System Status & Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Health Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-3">
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-3 shadow-lg">
                <Activity className="h-5 w-5 text-white" />
              </div>
              System Health
            </h3>
            <div
              className={`px-3 py-1.5 rounded-xl text-xs font-bold shadow-md ${
                healthToUse?.status === "healthy"
                  ? "bg-gradient-to-r from-emerald-300 to-green-400 text-white"
                  : healthToUse?.status === "warning"
                    ? "bg-gradient-to-r from-amber-300 to-orange-400 text-white"
                    : "bg-gradient-to-r from-red-300 to-red-400 text-white"
              }`}
            >
              {healthToUse?.status || "Unknown"}
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-bold text-slate-900">
                  Database Status
                </span>
                <span
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold shadow-md ${
                    healthToUse?.database?.healthy
                      ? "bg-gradient-to-r from-emerald-300 to-green-400 text-white"
                      : "bg-gradient-to-r from-red-300 to-red-400 text-white"
                  }`}
                >
                  {healthToUse?.database?.status || "Unknown"}
                </span>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-bold text-slate-900">
                  Memory Usage
                </span>
                <span className="text-sm font-bold text-slate-900">
                  {healthToUse?.system?.memory?.memoryUsage || 0}%
                </span>
              </div>
              <div className="w-full bg-gradient-to-r from-slate-50 to-blue-50 rounded-full h-3 overflow-hidden border border-slate-200/60 shadow-inner">
                <div
                  className={`h-full transition-all duration-300 shadow-md ${
                    (healthToUse?.system?.memory?.memoryUsage || 0) > 80
                      ? "bg-gradient-to-r from-red-400 to-red-500"
                      : (healthToUse?.system?.memory?.memoryUsage || 0) > 60
                        ? "bg-gradient-to-r from-amber-400 to-orange-500"
                        : "bg-gradient-to-r from-blue-400 to-indigo-500"
                  }`}
                  style={{
                    width: `${healthToUse?.system?.memory?.memoryUsage || 0}%`,
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-bold text-slate-900">
                  CPU Usage
                </span>
                <span className="text-sm font-bold text-slate-900">
                  {healthToUse?.system?.cpu?.percent || 0}%
                </span>
              </div>
              <div className="w-full bg-gradient-to-r from-slate-50 to-blue-50 rounded-full h-3 overflow-hidden border border-slate-200/60 shadow-inner">
                <div
                  className={`h-full transition-all duration-300 shadow-md ${
                    (healthToUse?.system?.cpu?.percent || 0) > 80
                      ? "bg-gradient-to-r from-red-400 to-red-500"
                      : (healthToUse?.system?.cpu?.percent || 0) > 60
                        ? "bg-gradient-to-r from-amber-400 to-orange-500"
                        : "bg-gradient-to-r from-purple-400 to-purple-500"
                  }`}
                  style={{
                    width: `${healthToUse?.system?.cpu?.percent || 0}%`,
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-bold text-slate-900">
                  API Response Time
                </span>
                <span className="text-sm font-bold text-emerald-600">
                  {Math.round(healthToUse?.api?.responseTime || 0)}ms
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Overview Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-3">
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-3 shadow-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              Financial Overview
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">
                Total Revenue
              </span>
              <span className="text-base font-bold text-emerald-600">
                {formatKSh(analyticsToUse?.financial?.totalRevenue || 0)}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">
                Total Expenses
              </span>
              <span className="text-base font-bold text-red-600">
                {formatKSh(analyticsToUse?.financial?.totalExpenses || 0)}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">
                Net Profit
              </span>
              <span
                className={`text-base font-bold ${
                  (analyticsToUse?.financial?.netProfit || 0) > 0
                    ? "text-emerald-600"
                    : "text-red-600"
                }`}
              >
                {formatKSh(analyticsToUse?.financial?.netProfit || 0)}
              </span>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-bold text-slate-900">
                  Collection Rate
                </span>
                <span className="text-sm font-bold text-blue-600">
                  {analyticsToUse?.financial?.collectionRate || 0}%
                </span>
              </div>
              <div className="w-full bg-gradient-to-r from-slate-50 to-blue-50 rounded-full h-3 overflow-hidden border border-slate-200/60 shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 transition-all duration-300 shadow-md"
                  style={{
                    width: `${analyticsToUse?.financial?.collectionRate || 0}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              User Distribution by Role
            </h3>
          </div>

          <div className="space-y-4">
            {analyticsToUse?.user?.usersByRole &&
              Object.entries(analyticsToUse.user.usersByRole).map(
                ([role, count]) => (
                  <div key={role} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-900 capitalize">
                        {role}s
                      </span>
                      <span className="text-sm font-bold text-slate-900">
                        {count as number}
                      </span>
                    </div>
                    <div className="w-full bg-gradient-to-r from-slate-50 to-blue-50 rounded-full h-3 overflow-hidden border border-slate-200/60 shadow-inner">
                      <div
                        className={`h-full transition-all duration-300 shadow-md ${
                          role === "superadmin"
                            ? "bg-gradient-to-r from-purple-400 to-purple-500"
                            : role === "admin"
                              ? "bg-gradient-to-r from-blue-400 to-indigo-500"
                              : role === "teacher"
                                ? "bg-gradient-to-r from-emerald-400 to-green-500"
                                : role === "student"
                                  ? "bg-gradient-to-r from-indigo-400 to-indigo-500"
                                  : "bg-gradient-to-r from-amber-400 to-orange-500"
                        }`}
                        style={{
                          width: `${
                            ((count as number) /
                              (analyticsToUse?.user?.totalUsers || 1)) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                ),
              )}
            {(!analyticsToUse?.user?.usersByRole ||
              Object.keys(analyticsToUse.user.usersByRole).length === 0) && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl shadow-lg mb-3">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <p className="text-sm text-slate-600 font-semibold">
                  No user role data available
                </p>
              </div>
            )}
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-3">
              <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-3 shadow-lg">
                <Server className="h-5 w-5 text-white" />
              </div>
              System Information
            </h3>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">Platform</span>
              <span className="text-sm font-bold text-slate-900 capitalize">
                {healthToUse?.system?.memory?.platform || "Unknown"}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">
                Architecture
              </span>
              <span className="text-sm font-bold text-slate-900">
                {healthToUse?.system?.memory?.arch || "Unknown"}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">
                CPU Cores
              </span>
              <span className="text-sm font-bold text-blue-600">
                {healthToUse?.system?.memory?.cpuCount || 0}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">
                Total Memory
              </span>
              <span className="text-sm font-bold text-purple-600">
                {Math.round(
                  (healthToUse?.system?.memory?.totalMemory || 0) / 1024,
                )}{" "}
                GB
              </span>
            </div>

            <div className="flex justify-between items-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
              <span className="text-sm font-bold text-slate-900">
                Node Version
              </span>
              <span className="text-sm font-bold text-emerald-600">
                {healthToUse?.system?.nodeVersion || "Unknown"}
              </span>
            </div>

            {/* Database Collections */}
            {healthToUse?.database?.collections && (
              <div className="pt-4 border-t border-slate-200">
                <h4 className="text-sm font-bold text-slate-900 mb-3">
                  Database Collections
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(healthToUse.database.collections).map(
                    ([collection, count]) => (
                      <div
                        key={collection}
                        className="flex justify-between items-center p-2 rounded-lg bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60"
                      >
                        <span className="text-xs font-bold text-slate-900 capitalize">
                          {collection}
                        </span>
                        <span className="text-xs font-bold text-blue-600">
                          {count as number}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Manual Invoice Generation */}
      <ManualInvoiceGeneration />

      {/* Sync Token Generator */}
      <SyncTokenGenerator />
    </div>
  );
};

export default SuperAdminDashboard;
